import { filter, find, get, includes, lt, sumBy, chain, isNumber, reduce, map } from 'lodash';
import {
  ME_EMPLOYEE_TYPES,
  PaxPlan,
  ProductContent,
  PRODUCT_TYPE,
  QuoteStore,
  TravellerParams,
  TravellerProduct
} from '@trip-ux/redux-store';
import { abs } from 'mathjs';
import { checkTrue, formatCurrencyWithLabel } from '@trip-ux/common';
import { SUMMARY_VALUE_TYPES } from '@trip-ux/constants';

const tripInterruptionPlans = ['STCIH'];
export const hasTripInteruption = (plancode: string): boolean => {
  return includes(tripInterruptionPlans, plancode);
};

const deductiblePlans = ['SEMH', 'MEMH', 'SVTCBH', 'SVTCSH', 'SVTCEH', 'SEMTUH'];
export const hasDeductible = (plancode: string, tripParam: TravellerParams): boolean => {
  return includes(deductiblePlans, plancode) ? get(get(tripParam, 'params'), 'deductible', 0) > 0 : false;
};

const coverageAmountPlans = ['SVTCBH', 'SVTCSH', 'SVTCEH', 'STCIH', 'MAIH'];
export const hasCoverageAmount = (plancode: string): boolean => {
  return includes(coverageAmountPlans, plancode);
};

export const getDiscount = (breakdownType: string, data: TravellerProduct, quoteRes: PaxPlan): number => {
  let adjustments;
  if (get(data, 'type') === PRODUCT_TYPE.PLAN) {
    adjustments = find(get(quoteRes, 'adjustments'), { breakdownType });
    return get(adjustments, 'amount', 0);
  } else {
    adjustments = get(find(get(quoteRes, 'optionalBenefits', []), { code: get(data, 'code') }), 'adjustments', []);
    return sumBy(
      filter(adjustments, adj => lt(adj.amount, 0)),
      'amount'
    );
  }
};

export const getRiderDiscount = (breakdownType: string, code: string, quoteRes: PaxPlan): number => {
  const resultArray = map(
    filter(get(quoteRes, 'optionalBenefits'), obj => obj.code === code),
    obj => ({ code: obj.code, adjustments: obj.adjustments })
  );
  const adjustments = find(get(resultArray[0], 'adjustments'), { breakdownType });
  return abs(get(adjustments, 'amount', 0));
};

export const getPaxTotalPlanCost = (paxId: number, travellersProducts: TravellerProduct[]): number => {
  const productsSelected = filter(travellersProducts, product => product.paxId === paxId);
  return chain(productsSelected)
    .filter(prod => isNumber(prod.total))
    .flatMap('total')
    .sum()
    .value();
};

export const getTotalPlanCost = (quoteData: QuoteStore, travellersProducts: TravellerProduct[]): number => {
  return reduce(
    quoteData,
    (acc, quote) => {
      const paxId = parseInt(String(get(quote, 'referenceNumber')), 10);
      const totalPlanCost = getPaxTotalPlanCost(paxId, travellersProducts);
      return acc + (totalPlanCost || 0);
    },
    0
  );
};

export const getAddOn = (paxId: number, travellersProducts: TravellerProduct[]): TravellerProduct[] => {
  return filter(
    travellersProducts,
    product => product.paxId === paxId && product.type === PRODUCT_TYPE.OPTIONAL_BENEFIT
  );
};

export const getPlanSelectionRowAmount = (type: string, plan: TravellerProduct, quoteRes: PaxPlan): number => {
  return abs(getDiscount(type, plan, quoteRes));
};

export const getCoverageType = (labels, isFamily, multiTrip): string => {
  const { multiTrip: multiTripLabel, singleTrip: singleTripLabel, familyCoverageLabel } = labels;

  let coverageType = singleTripLabel;
  if (isFamily) {
    coverageType = familyCoverageLabel;
  } else if (checkTrue(multiTrip)) {
    coverageType = multiTripLabel;
  }
  return coverageType;
};

const formatSummaryAmount = (hasNegativeSymbol: boolean, value: number, locale: string): string => {
  return hasNegativeSymbol ? `-${formatCurrencyWithLabel(value, locale)}` : formatCurrencyWithLabel(value, locale);
};

export const formatSummaryValue = (
  value: number,
  hasNegativeSymbol: boolean,
  locale: string,
  type: string,
  planSelected: ProductContent
): string => {
  switch (type) {
    case SUMMARY_VALUE_TYPES.COVERAGE_AMOUNT: {
      const coverageAmounts = get(planSelected, 'coverageAmounts.default', []);
      const coverageAmount = find(coverageAmounts, { value: value.toString() });
      const unlimitedVal = '999999';
      if (get(coverageAmount, 'value') === unlimitedVal) {
        return get(coverageAmount, 'label');
      } else {
        return formatSummaryAmount(hasNegativeSymbol, value, locale);
      }
    }
    case SUMMARY_VALUE_TYPES.AMOUNT:
      return formatSummaryAmount(hasNegativeSymbol, value, locale);
    case SUMMARY_VALUE_TYPES.DAYS: {
      const tripDaysOptions = get(planSelected, 'tripDays', []);
      const tripDays = find(tripDaysOptions, { value: value.toString() });
      return get(tripDays, 'label');
    }
    default:
      return '';
  }
};

export enum COVERME_PLAN_BREAKDOWN_TYPES {
  DEDUCTIBLE = 'DEDUCTIBLE',
  COMPANIONDISCOUNT = 'COMPANION',
  TRAVELLINGWITHINCANADA = 'TRAVELLINGWITHINCANADA',
  SMOKERSURCHARGE = 'SMOKERSURCHARGE',
  EMPLOYEE = 'EMPLOYEE',
  FAMILYFRIENDSRESIDING = 'FAMILYFRIENDSRESIDING',
  FAMILYFRIENDSNOTRESIDING = 'FAMILYFRIENDSNOTRESIDING'
}

enum DISCOUNT_TMS_KEY {
  EMPLOYEE = 'employeeDiscount',
  FAMILYFRIENDSRESIDING = 'fnfResidingDiscount',
  FAMILYFRIENDSNOTRESIDING = 'fnfNotResidingDiscount'
}

export const getEmployeeOrFnfDiscount = (
  employeeType: ME_EMPLOYEE_TYPES,
  plan: TravellerProduct,
  quoteRes: PaxPlan
): { amount: number; tmsKey: string } => {
  switch (employeeType) {
    case ME_EMPLOYEE_TYPES.MANULIFE_EMPLOYEE:
      return {
        amount: getPlanSelectionRowAmount(COVERME_PLAN_BREAKDOWN_TYPES.EMPLOYEE, plan, quoteRes),
        tmsKey: DISCOUNT_TMS_KEY.EMPLOYEE
      };
    case ME_EMPLOYEE_TYPES.RESIDE_WITH_MANULIFE_EMPLOYEE:
      return {
        amount: getPlanSelectionRowAmount(COVERME_PLAN_BREAKDOWN_TYPES.FAMILYFRIENDSRESIDING, plan, quoteRes),
        tmsKey: DISCOUNT_TMS_KEY.FAMILYFRIENDSRESIDING
      };
    case ME_EMPLOYEE_TYPES.NOT_RESIDE_WITH_MANULIFE_EMPLOYEE:
      return {
        amount: getPlanSelectionRowAmount(COVERME_PLAN_BREAKDOWN_TYPES.FAMILYFRIENDSNOTRESIDING, plan, quoteRes),
        tmsKey: DISCOUNT_TMS_KEY.FAMILYFRIENDSNOTRESIDING
      };
    default:
      return {
        amount: 0,
        tmsKey: ''
      };
  }
};

export const getAddOnEmployeeOrFnfDiscount = (
  employeeType: ME_EMPLOYEE_TYPES,
  addOnCode: string,
  quoteRes: PaxPlan
): { amount: number; tmsKey: string } => {
  switch (employeeType) {
    case ME_EMPLOYEE_TYPES.MANULIFE_EMPLOYEE:
      return {
        amount: getRiderDiscount(COVERME_PLAN_BREAKDOWN_TYPES.EMPLOYEE, addOnCode, quoteRes),
        tmsKey: DISCOUNT_TMS_KEY.EMPLOYEE
      };
    case ME_EMPLOYEE_TYPES.RESIDE_WITH_MANULIFE_EMPLOYEE:
      return {
        amount: getRiderDiscount(COVERME_PLAN_BREAKDOWN_TYPES.FAMILYFRIENDSRESIDING, addOnCode, quoteRes),
        tmsKey: DISCOUNT_TMS_KEY.FAMILYFRIENDSRESIDING
      };
    case ME_EMPLOYEE_TYPES.NOT_RESIDE_WITH_MANULIFE_EMPLOYEE:
      return {
        amount: getRiderDiscount(COVERME_PLAN_BREAKDOWN_TYPES.FAMILYFRIENDSNOTRESIDING, addOnCode, quoteRes),
        tmsKey: DISCOUNT_TMS_KEY.FAMILYFRIENDSNOTRESIDING
      };
    default:
      return {
        amount: 0,
        tmsKey: ''
      };
  }
};
